import {
  Divider,
  Flex,
  FormHelperText,
  Input,
  InputGroup,
  InputRightAddon,
  SimpleGrid,
  Textarea
} from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'

import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { SectionTitle } from '../../../components/SectionCard'
import { FormSelect, SearchableSelect } from '../../../components/Select'
import { statusOptions } from '../../../components/StatusBadge'
import { useAppToast } from '../../../helpers'
import {
  IScheduleClass,
  IScheduleClassPayload,
  IScheduleFormOptions,
  TScheduleCategory,
  TScheduleFormat,
  TScheduleShift,
  TScheduleStatus,
  TScheduleTerm
} from '../../../services/ScheduleClasses/interfaces'
import ScheduleClassService from '../../../services/ScheduleClasses/ScheduleClassService'
import {
  CATEGORY_LABELS,
  DEFAULT_MAX_STUDENTS,
  FORMAT_LABELS,
  SCHEDULE_STATUS,
  SHIFT_LABELS,
  TEACHER_PLACEHOLDER,
  TERM_LABELS,
  termForMonth
} from './constants'

interface IProps {
  isOpen: boolean
  onClose: () => void
  /** Ano e período da aba aberta: só o ponto de partida do formulário e o rótulo do cabeçalho. */
  year: number
  term: TScheduleTerm
  scheduleClass?: IScheduleClass | null
  mode: 'create' | 'edit'
  options: IScheduleFormOptions
  /** Recebe a turma como o backend a gravou — o período dela pode não ser o da aba. */
  onSuccess: (saved: IScheduleClass) => void
}

/**
 * Estado local do formulário: tudo string, como os inputs entregam.
 *
 * category admite '' porque turma importada chega com ela nula — a planilha não tem essa coluna.
 * '' é o que mantém o Select em branco em vez de exibir a primeira opção como se fosse um valor
 * salvo.
 */
interface IFormState {
  course_id: string
  teacher_id: string
  code: string
  room: string
  start_date: string
  end_date: string
  week_days: string
  shift: TScheduleShift
  /** '' porque turma importada chega sem formato, pela mesma razão de category. */
  format: TScheduleFormat | ''
  start_time: string
  end_time: string
  enrolled_students: string
  max_students: string
  category: TScheduleCategory | ''
  status: TScheduleStatus
  notes: string
}

const baseForm: IFormState = {
  course_id: '',
  teacher_id: '',
  code: '',
  room: '',
  start_date: '',
  end_date: '',
  week_days: '',
  shift: 'night',
  // Formato não tem padrão, como categoria: "Presencial" preselecionado rotularia errado
  // qualquer turma online que passasse batido, e são 355 delas nas 814 linhas da planilha.
  format: '',
  start_time: '',
  end_time: '',
  enrolled_students: '0',
  max_students: String(DEFAULT_MAX_STUDENTS),
  // Categoria não tem padrão: as três opções são decisões diferentes de negócio e deixar
  // "Cursos Livres" preselecionado rotula errado qualquer módulo do DI que passe batido.
  category: '',
  // 'pending', e não 'confirmed' (ABRA-315): confirmar deixou de ser um clique e passou a ser
  // consequência de metade das vagas preenchidas. Nascer confirmada com zero matriculados era
  // o padrão de quando o campo era só um rótulo, e tornaria a regra invisível — toda turma
  // nova já estaria no estado que ela existe para conceder.
  status: 'pending',
  notes: ''
}

/**
 * Os campos do formulário que carregam número. Nomeados num union próprio, e não em
 * `keyof IFormState`, porque a conferência abaixo chama `.trim()` no valor: com a chave aberta,
 * incluir aqui um campo que não seja texto passaria pela tupla e só quebraria no uso.
 */
type TNumericField = 'enrolled_students' | 'max_students'

/**
 * Faixa que o backend aceita em cada campo numérico, com as mesmas mensagens dele.
 *
 * O atributo `min` dos inputs não segura nada: quem envia é o handler do botão Salvar, não o
 * validador nativo do formulário, então dá para digitar `-5` nas vagas e o valor chega ao
 * payload. Conferir aqui troca um round-trip com 422 por um campo destacado.
 * O teto vem junto porque `type="number"` também aceita decimal e número gigante.
 */
const NUMERIC_RANGES: Array<[TNumericField, number, number, string]> = [
  ['enrolled_students', 0, 99999, 'O número de alunos matriculados deve ser um número inteiro de 0 a 99999.'],
  ['max_students', 0, 99999, 'O número de vagas máximas deve ser um número inteiro de 0 a 99999.']
]

/**
 * Campos obrigatórios que a tela consegue conferir sozinha, com as mesmas mensagens da API.
 *
 * O backend continua sendo a autoridade; isto existe para a categoria em branco parar aqui,
 * apontando o campo, em vez de voltar num 422 genérico numa turma importada que a pessoa abriu
 * só para vincular curso ou professor.
 */
function validate(form: IFormState): Record<string, string[]> {
  const errors: Record<string, string[]> = {}

  const required: Array<[keyof IFormState, string]> = [
    ['start_date', 'A data de início é obrigatória.'],
    ['end_date', 'A data de fim é obrigatória.'],
    ['week_days', 'Os dias da semana são obrigatórios.'],
    ['start_time', 'O horário inicial é obrigatório.'],
    ['end_time', 'O horário final é obrigatório.'],
    ['format', 'O formato da turma é obrigatório.'],
    ['category', 'A categoria é obrigatória.']
  ]

  required.forEach(([field, message]) => {
    if (!form[field].trim()) errors[field] = [message]
  })

  if (form.start_date && form.end_date && form.end_date < form.start_date) {
    errors.end_date = ['A data de fim não pode ser anterior à data de início.']
  }

  // A mesma conferência das datas, para as horas. O backend exige `after:start_time`, e sem isto
  // uma faixa como 19h–19h ou 22h–19h só era recusada depois da requisição. Comparação de texto
  // basta: <input type="time"> entrega sempre "HH:MM" com zero à esquerda.
  if (form.start_time && form.end_time && form.end_time <= form.start_time) {
    errors.end_time = ['O horário final deve ser posterior ao horário inicial.']
  }

  NUMERIC_RANGES.forEach(([field, min, max, message]) => {
    const raw = form[field].trim()

    // Vazio é assunto da lista de obrigatórios acima, que já pode ter escrito uma mensagem
    // melhor neste campo — sobrescrever trocaria "é obrigatória" por "deve ser um número".
    if (raw === '' || errors[field]) return

    const value = Number(raw)

    if (!Number.isInteger(value) || value < min || value > max) errors[field] = [message]
  })

  return errors
}

/**
 * Cadastro e edição de turma (ABRA-181).
 *
 * Os campos ficam agrupados em seções — é o que resolve o principal problema da tela
 * do WordPress, que os enfileirava sem hierarquia.
 *
 * Curso e professor podem ficar vazios: salvar é permitido e a turma passa a aparecer
 * destacada como cadastro incompleto.
 */
export default function ScheduleClassFormModal({
  isOpen,
  onClose,
  year,
  term,
  scheduleClass,
  mode,
  options,
  onSuccess
}: IProps) {
  const toast = useAppToast()
  const service = useMemo(() => new ScheduleClassService(), [])
  const [form, setForm] = useState<IFormState>(baseForm)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({})
  const [saving, setSaving] = useState(false)

  const courseOptions = useMemo(
    () => options.courses.map((c) => ({ value: String(c.id), label: c.name })),
    [options.courses]
  )
  const teacherOptions = useMemo(
    () => options.teachers.map((t) => ({ value: String(t.id), label: t.name })),
    [options.teachers]
  )

  const courseDuration = useMemo(() => {
    if (!form.course_id) return null

    return options.courses.find((c) => String(c.id) === form.course_id)?.duration_in_hours ?? null
  }, [form.course_id, options.courses])

  /**
   * O curso selecionado ainda é o que a turma já tinha.
   *
   * É a condição da preservação abaixo, e não `mode === 'edit'`: a carga guardada pertence ao
   * vínculo que a produziu, então ela só continua valendo enquanto o vínculo é o mesmo.
   */
  const keepsCourse = mode === 'edit' && String(scheduleClass?.course_id ?? '') === form.course_id

  /**
   * Carga horária que a turma vai receber, só para o campo em somente leitura não mentir.
   *
   * Espelha exatamente o que ScheduleClassController::derivedFromCourseAndSeats grava: a duração
   * do curso quando ela existe e, quando não, o valor já gravado — mas só se o curso não mudou.
   *
   * As duas metades dessa regra têm motivos opostos e é por isso que ela não é um `??` simples.
   * Preservar no mesmo curso é o que impede a edição de uma sala de apagar a carga de uma turma
   * antiga. Não preservar na troca é o que impede a turma de levar para o curso novo a carga do
   * antigo — que seria a mesma divergência entre turmas do mesmo curso que a derivação existe
   * para acabar.
   */
  const workload = courseDuration ?? (keepsCourse ? (scheduleClass?.workload ?? null) : null)

  /**
   * A frase sob o campo, que tem de concordar com o número acima dela.
   *
   * O caso do meio é o que não existia: com o curso sem duração mas a carga preservada, dizer
   * "não tem duração cadastrada" ao lado de um "30" deixava a tela se contradizendo.
   */
  const workloadHint = !form.course_id
    ? 'Vem da duração do curso, depois que um for selecionado.'
    : courseDuration !== null
      ? 'Vem da duração do curso selecionado.'
      : workload !== null
        ? 'O curso não tem duração cadastrada; a turma mantém a carga já gravada.'
        : 'O curso selecionado não tem duração cadastrada. Defina-a no cadastro do curso.'

  useEffect(() => {
    if (!isOpen) return
    setFieldErrors({})

    if (mode === 'edit' && scheduleClass) {
      setForm({
        course_id: scheduleClass.course_id ? String(scheduleClass.course_id) : '',
        teacher_id: scheduleClass.teacher_id ? String(scheduleClass.teacher_id) : '',
        code: scheduleClass.code ?? '',
        room: scheduleClass.room ?? '',
        start_date: scheduleClass.start_date,
        end_date: scheduleClass.end_date,
        week_days: scheduleClass.week_days,
        shift: scheduleClass.shift,
        // Nula em turma importada de planilha sem a coluna "Formato": fica em branco, e não
        // na primeira opção do Select, para o formulário não gravar uma modalidade inventada.
        format: scheduleClass.format ?? '',
        start_time: scheduleClass.start_time.slice(0, 5),
        end_time: scheduleClass.end_time.slice(0, 5),
        enrolled_students: String(scheduleClass.enrolled_students),
        max_students: scheduleClass.max_students === null ? '' : String(scheduleClass.max_students),
        // Turma importada chega sem categoria: a planilha não tem essa coluna. Nula fica em
        // branco, e não na primeira opção do Select — preencher gravava uma categoria inventada
        // em qualquer turma importada que alguém abrisse só para vincular curso ou professor,
        // sem nada na tela denunciando que aquele valor tinha sido escolhido pelo formulário.
        category: scheduleClass.category ?? '',
        status: scheduleClass.status,
        notes: scheduleClass.notes ?? ''
      })
      return
    }

    setForm(baseForm)
  }, [isOpen, mode, scheduleClass])

  function setField<K extends keyof IFormState>(key: K, value: IFormState[K]) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  function buildPayload(): IScheduleClassPayload {
    return {
      course_id: form.course_id ? Number(form.course_id) : null,
      teacher_id: form.teacher_id ? Number(form.teacher_id) : null,
      start_date: form.start_date,
      end_date: form.end_date,
      week_days: form.week_days,
      shift: form.shift,
      // Só chega aqui depois de validate(), que garante o formato preenchido.
      format: form.format as TScheduleFormat,
      start_time: form.start_time,
      end_time: form.end_time,
      room: form.room || null,
      code: form.code || null,
      enrolled_students: form.enrolled_students ? Number(form.enrolled_students) : 0,
      max_students: form.max_students ? Number(form.max_students) : null,
      // Só chega aqui depois de validate(), que garante a categoria preenchida.
      category: form.category as TScheduleCategory,
      status: form.status,
      notes: form.notes.trim() || null
    }
  }

  async function handleSubmit() {
    // Editar sem turma carregada não deveria acontecer, mas cair no create criaria uma duplicata:
    // a versão anterior deste método simplesmente não fazia requisição nenhuma neste caso.
    if (mode === 'edit' && !scheduleClass) return

    const errors = validate(form)

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      toast.error('Não foi possível salvar a turma', 'Confira os campos destacados.')

      return
    }

    setSaving(true)
    setFieldErrors({})

    try {
      const payload = buildPayload()

      const { data: saved } =
        mode === 'edit' && scheduleClass
          ? await service.update(scheduleClass.id, payload)
          : await service.create(payload)

      const moved = saved.year !== year || saved.term !== term

      // O período sai da data de início, então a turma pode não pertencer à aba aberta.
      // Sem dizer para onde ela foi, o desfecho seria uma linha que some da tabela.
      toast.success(
        mode === 'create' ? 'Turma criada' : 'Turma atualizada',
        moved ? `A turma ficou em ${TERM_LABELS[saved.term]} ${saved.year}.` : undefined
      )
      onSuccess(saved)
      onClose()
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { errors?: Record<string, string[]>; message?: string } } }
      setFieldErrors(axiosError?.response?.data?.errors ?? {})
      toast.error('Não foi possível salvar a turma', 'Confira os campos destacados.')
    } finally {
      setSaving(false)
    }
  }

  const showCourseHint = !form.course_id && scheduleClass?.course_name_raw
  const showTeacherHint = !form.teacher_id && scheduleClass?.teacher_name_raw

  /**
   * Ano e período que a turma vai receber, calculados aqui só para o cabeçalho não mentir
   * enquanto a pessoa digita. Quem decide de verdade é o backend, com o mesmo corte
   * (ScheduleClass::termFor). Sem data ainda, mostra a aba de onde o modal foi aberto.
   */
  const target = useMemo(() => {
    const [targetYear, month] = form.start_date.split('-').map(Number)

    return targetYear && month ? { year: targetYear, term: termForMonth(month) } : { year, term }
  }, [form.start_date, year, term])

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={`${mode === 'create' ? 'Nova turma' : 'Editar turma'} — ${TERM_LABELS[target.term]} ${target.year}`}
      onSubmit={handleSubmit}
      size="wide"
      isLoading={saving}
    >
      <Flex direction="column" gap={4}>
        <SectionTitle>Identificação</SectionTitle>
        <Divider />

        {/* Formato ao lado do curso: é a modalidade DAQUELE curso naquela turma, e a
                pessoa decide as duas coisas junto. Ficava em Classificação, longe do curso. */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <FormField label="Curso" error={fieldErrors.course_id?.[0]}>
            <SearchableSelect
              options={courseOptions}
              value={form.course_id}
              onChange={(value) => setField('course_id', value)}
              placeholder="Selecione o curso"
              isClearable
            />
            {showCourseHint && (
              <FormHelperText>
                Na planilha: <b>{scheduleClass?.course_name_raw}</b>
              </FormHelperText>
            )}
          </FormField>

          <FormField label="Formato" error={fieldErrors.format?.[0]} isRequired>
            <FormSelect
              value={form.format}
              onChange={(value) => setField('format', value as TScheduleFormat | '')}
              options={Object.entries(FORMAT_LABELS).map(([value, label]) => ({ value: value, label: label }))}
              placeholder="Selecione"
            />
          </FormField>
        </SimpleGrid>

        <FormField label="Professor" error={fieldErrors.teacher_id?.[0]}>
          <SearchableSelect
            options={teacherOptions}
            value={form.teacher_id}
            onChange={(value) => setField('teacher_id', value)}
            placeholder="Selecione o professor"
            isClearable
          />
          {/* Sem professor a turma não fica em branco na listagem: aparece como "A Definir",
              com o aviso de vínculo pendente ao lado. Dizê-lo aqui evita a leitura de que
              deixar vazio esconde a turma. */}
          {!form.teacher_id && !showTeacherHint && (
            <FormHelperText>
              A turma aparece como <b>{TEACHER_PLACEHOLDER}</b> enquanto não houver professor vinculado.
            </FormHelperText>
          )}
          {showTeacherHint && (
            <FormHelperText>
              Na planilha: <b>{scheduleClass?.teacher_name_raw}</b>
            </FormHelperText>
          )}
        </FormField>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {/* maxLength espelha o limite da coluna: barrar a digitação é melhor que aceitar o
                  texto e devolver 422 no Salvar. */}
          <FormField label="Código" error={fieldErrors.code?.[0]}>
            <Input value={form.code} maxLength={100} onChange={(e) => setField('code', e.target.value)} />
          </FormField>
          <FormField label="Sala" error={fieldErrors.room?.[0]}>
            <Input value={form.room} maxLength={100} onChange={(e) => setField('room', e.target.value)} />
          </FormField>
        </SimpleGrid>

        <SectionTitle>Período e horário</SectionTitle>
        <Divider />

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <FormField label="Início" error={fieldErrors.start_date?.[0]} isRequired>
            <Input type="date" value={form.start_date} onChange={(e) => setField('start_date', e.target.value)} />
            <FormHelperText>
              Define o ano e o período da turma: ela será salva em <b>{TERM_LABELS[target.term]}</b> de{' '}
              <b>{target.year}</b>.
            </FormHelperText>
          </FormField>
          <FormField label="Fim" error={fieldErrors.end_date?.[0]} isRequired>
            <Input type="date" value={form.end_date} onChange={(e) => setField('end_date', e.target.value)} />
          </FormField>
        </SimpleGrid>

        <FormField label="Dias da semana" error={fieldErrors.week_days?.[0]} isRequired>
          <Input
            placeholder="Ex.: Segunda e Quarta"
            value={form.week_days}
            maxLength={255}
            onChange={(e) => setField('week_days', e.target.value)}
          />
        </FormField>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <FormField label="Período da turma" error={fieldErrors.shift?.[0]} isRequired>
            <FormSelect
              value={form.shift}
              onChange={(value) => setField('shift', value as TScheduleShift)}
              options={Object.entries(SHIFT_LABELS).map(([value, label]) => ({ value: value, label: label }))}
              hideEmptyOption
            />
          </FormField>
          <FormField label="Das" error={fieldErrors.start_time?.[0]} isRequired>
            <Input type="time" value={form.start_time} onChange={(e) => setField('start_time', e.target.value)} />
          </FormField>
          <FormField label="Até" error={fieldErrors.end_time?.[0]} isRequired>
            <Input type="time" value={form.end_time} onChange={(e) => setField('end_time', e.target.value)} />
          </FormField>
        </SimpleGrid>

        {/*
          Somente leitura: a carga horária é a do curso, e digitá-la turma a turma é o que
          fazia o mesmo curso aparecer com 30 h numa linha e 40 h na outra (ABRA-315). Quem
          grava é o backend, a partir de `courses.duration_in_hours`; este campo existe para
          a pessoa conferir o número antes de salvar, não para mudá-lo.
        */}
        <FormField label="Carga horária" maxW={{ md: '260px' }}>
          <InputGroup>
            <Input value={workload === null ? '' : String(workload)} isReadOnly placeholder="—" />
            <InputRightAddon>horas</InputRightAddon>
          </InputGroup>
          <FormHelperText>{workloadHint}</FormHelperText>
        </FormField>

        <SectionTitle>Vagas</SectionTitle>
        <Divider />

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <FormField label="Alunos matriculados" error={fieldErrors.enrolled_students?.[0]}>
            <Input
              type="number"
              min={0}
              value={form.enrolled_students}
              onChange={(e) => setField('enrolled_students', e.target.value)}
            />
          </FormField>
          <FormField label="Vagas máximas" error={fieldErrors.max_students?.[0]}>
            <Input
              type="number"
              min={0}
              value={form.max_students}
              onChange={(e) => setField('max_students', e.target.value)}
            />
          </FormField>
        </SimpleGrid>

        <SectionTitle>Classificação</SectionTitle>
        <Divider />

        {/* Duas colunas, como o resto do modal: "Tipo" saiu daqui (a API deriva course_type
                da data de início) e o grid de 3 deixava Categoria e Status estreitos, com uma
                coluna vazia à direita. */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <FormField label="Categoria" error={fieldErrors.category?.[0]} isRequired>
            <FormSelect
              value={form.category}
              onChange={(value) => setField('category', value as TScheduleCategory | '')}
              options={Object.entries(CATEGORY_LABELS).map(([value, label]) => ({
                value: value,
                label: label
              }))}
              placeholder="Selecione"
            />
          </FormField>
          <FormField label="Status" error={fieldErrors.status?.[0]} isRequired>
            <FormSelect
              value={form.status}
              onChange={(value) => setField('status', value as TScheduleStatus)}
              options={statusOptions(SCHEDULE_STATUS)}
              hideEmptyOption
            />
            {/* O status escolhido é uma intenção: a partir de metade das vagas o backend
                confirma a turma, e ao lotar marca as vagas como esgotadas. Cancelada e
                Prorrogada são decisões e não são reescritas. */}
            <FormHelperText>
              A turma se confirma sozinha a partir de metade das vagas e passa a <b>Vagas Esgotadas</b> quando elas
              acabam. <b>Cancelada</b> e <b>Prorrogada</b> não mudam sozinhas.
            </FormHelperText>
          </FormField>
        </SimpleGrid>

        <FormField label="Observação" error={fieldErrors.notes?.[0]}>
          <Textarea
            value={form.notes}
            rows={3}
            placeholder="Intervalo de almoço, material que o aluno deve trazer, aula a repor..."
            onChange={(e) => setField('notes', e.target.value)}
          />
        </FormField>
      </Flex>
    </FormModal>
  )
}
